import { useEffect, useState } from "react";
import { useTranslation } from "@thyra/landing-i18n";
import { Lock } from "lucide-react";
import { MobileAppPreview } from "./showcase/MobileAppPreview";
import { type ShowcasePhase, WebAppPreview } from "./showcase/WebAppPreview";

/**
 * Hero product showcase: the product as it actually ships, on both platforms.
 * A browser window holds the web app's Approvals page and a phone holds the
 * mobile app's Tasks & Approvals tab, both showing the same queue. One
 * approval cycles pending → approving → done: it is tapped on the phone and
 * leaves the pending queue on the web.
 */

const PHASE_MS: Record<ShowcasePhase, number> = {
  // Matches the `showcase-tap` ripple, which spans the whole pending phase.
  pending: 3400,
  approving: 1100,
  done: 2800,
};

const NEXT_PHASE: Record<ShowcasePhase, ShowcasePhase> = {
  pending: "approving",
  approving: "done",
  done: "pending",
};

function usePhaseCycle(): ShowcasePhase {
  const [phase, setPhase] = useState<ShowcasePhase>("pending");

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) return;
    const t = setTimeout(() => setPhase(NEXT_PHASE[phase]), PHASE_MS[phase]);
    return () => clearTimeout(t);
  }, [phase]);

  return phase;
}

export function HeroShowcase() {
  const { t } = useTranslation("home");
  const phase = usePhaseCycle();

  return (
    <div className="xl:-mx-12">
      {/* One image to assistive tech; `inert` keeps the real buttons and
          checkboxes inside the previews out of the tab order. */}
      <div
        role="img"
        aria-label={t("heroPreview.aria")}
        className="relative mx-auto w-full md:pb-16 md:pr-72"
      >
        <div inert>
          <div
            className="hero-card-enter relative"
            style={{ animationDelay: "280ms" }}
          >
            <div className="showcase-glow" aria-hidden />

            <div className="showcase-panel relative overflow-hidden">
              {/* Browser chrome */}
              <div className="flex items-center gap-3 border-b border-border-subtle bg-background-muted/70 px-4 py-2.5 dark:bg-black/20">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
                </div>
                <div className="mx-auto flex h-6 min-w-0 items-center gap-1.5 rounded-md border border-border-subtle bg-background px-3 text-[11px] font-medium text-muted-foreground dark:bg-white/5">
                  <Lock className="h-3 w-3 shrink-0" strokeWidth={2} />
                  <span className="truncate">app.thyra-ai.com/approvals</span>
                </div>
                {/* Balances the traffic lights so the address stays centred. */}
                <span className="w-[42px]" />
              </div>

              <WebAppPreview phase={phase} />
            </div>
          </div>

          <div
            className="hero-card-enter relative z-10 mx-auto -mt-12 w-fit md:absolute md:right-0 md:top-12 md:mt-0"
            style={{ animationDelay: "480ms" }}
          >
            <MobileAppPreview phase={phase} />
          </div>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground sm:text-sm md:mt-2 md:pr-72">
        {t("heroPreview.caption")}
      </p>
    </div>
  );
}
